// Login Page - Green Door CRM
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import api from '../../services/api';
import { Loader2, Smartphone, Apple } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { usePWAInstall } from '../../hooks/usePWAInstall';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, ownerRegister } = useAuth();
  const [loading, setLoading] = useState(false);
  const { canInstall, isInstalled, isIOS, triggerInstall } = usePWAInstall();
  
  // Login form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Owner registration form
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [ownerConfirmPassword, setOwnerConfirmPassword] = useState('');
  const [ownerFirstName, setOwnerFirstName] = useState('');
  const [ownerLastName, setOwnerLastName] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch { toast.error('Failed to send reset link'); }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.first_name}!`);
      
      // Route based on role
      if (user.roles?.includes('system_owner')) {
        navigate('/admin/system');
      } else if (user.roles?.includes('owner')) {
        navigate('/owner-portal');
      } else if (user.roles?.some(r => ['tenant_card_runner', 'tenant_sale_clerk'].includes(r))) {
        navigate('/sales');
      } else if (user.roles?.includes('tenant_farm_manager')) {
        navigate('/horses');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOwnerRegister = async (e) => {
    e.preventDefault();
    if (ownerPassword !== ownerConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    
    try {
      await ownerRegister({
        email: ownerEmail,
        password: ownerPassword,
        first_name: ownerFirstName,
        last_name: ownerLastName,
        access_key: accessKey,
      });
      toast.success('Registration successful!');
      navigate('/owner-portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex flex-col items-center justify-center p-4">
      {/* Logo and Tagline */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-3">
          <img src="/logo.svg" alt="Green Door" className="w-32 h-32 object-contain" />
        </div>
        <p className="text-muted-foreground text-sm tracking-wide">
          Reining in your data to power your Stable and Sales
        </p>
      </div>

      <Card className="w-full max-w-md border-border shadow-card">
        <CardHeader className="text-center pb-4">
          <CardTitle className="font-serif text-2xl">Welcome</CardTitle>
          <CardDescription>Sign in to your account or register as an owner</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', width: '100%', height: 'auto', marginBottom: '1.5rem', overflow: 'hidden' }}>
              <TabsTrigger value="login" data-testid="login-tab" className="text-sm font-medium" style={{ whiteSpace: 'normal', height: 'auto', minHeight: '2.25rem', lineHeight: '1.3' }}>Sign In</TabsTrigger>
              <TabsTrigger value="owner" data-testid="owner-tab" className="text-sm font-medium" style={{ whiteSpace: 'normal', height: 'auto', minHeight: '2.25rem', lineHeight: '1.3' }}>Owner Registration</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>
                  ) : (
                    'Sign In'
                  )}
                </Button>
                <button
                  type="button"
                  className="w-full text-sm text-muted-foreground hover:text-foreground underline"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Forgot Password?
                </button>
                <p className="text-center text-sm text-muted-foreground pt-2">
                  New Consignor or Syndicate? <a href="/signup" className="text-primary hover:underline font-medium">Get Started</a>
                </p>
                <p className="text-center text-sm text-muted-foreground">
                  New Farm? <a href="/signup/farm" className="text-primary hover:underline font-medium">Get Started</a>
                </p>
              </form>
            </TabsContent>
            
            <TabsContent value="owner">
              <form onSubmit={handleOwnerRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ownerFirstName">First Name</Label>
                    <Input
                      id="ownerFirstName"
                      value={ownerFirstName}
                      onChange={(e) => setOwnerFirstName(e.target.value)}
                      required
                      data-testid="owner-firstname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ownerLastName">Last Name</Label>
                    <Input
                      id="ownerLastName"
                      value={ownerLastName}
                      onChange={(e) => setOwnerLastName(e.target.value)}
                      required
                      data-testid="owner-lastname-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ownerEmail">Email</Label>
                  <Input
                    id="ownerEmail"
                    type="email"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    required
                    data-testid="owner-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ownerPassword">Password</Label>
                  <Input
                    id="ownerPassword"
                    type="password"
                    value={ownerPassword}
                    onChange={(e) => setOwnerPassword(e.target.value)}
                    required
                    data-testid="owner-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ownerConfirmPassword">Confirm Password</Label>
                  <Input
                    id="ownerConfirmPassword"
                    type="password"
                    value={ownerConfirmPassword}
                    onChange={(e) => setOwnerConfirmPassword(e.target.value)}
                    required
                    data-testid="owner-confirm-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="accessKey">Access Key</Label>
                  <Input
                    id="accessKey"
                    placeholder="Enter one Access Key provided to you"
                    value={accessKey}
                    onChange={(e) => setAccessKey(e.target.value)}
                    required
                    className="font-mono"
                    data-testid="owner-accesskey-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Your Consignor, Farm, or Syndicate will provide this key to connect your account. Additional Access Keys may be connected once you are registered.
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="owner-register-btn"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Registering...</>
                  ) : (
                    'Register as Owner'
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="mt-8 text-center text-xs text-muted-foreground">
        <p>a <span className="font-semibold">Barn Door Software, LLC</span> product</p>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(v) => { setShowForgotPassword(v); if (!v) { setForgotSent(false); setForgotEmail(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{forgotSent ? 'Check Your Email' : 'Forgot Password'}</DialogTitle>
          </DialogHeader>
          {forgotSent ? (
            <div className="py-4 text-center">
              <p className="text-sm">If <strong>{forgotEmail}</strong> is registered, you will receive an email with a link to reset your password.</p>
              <p className="text-xs text-muted-foreground mt-2">The link will expire in 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">Enter your email and we'll send you a link to reset your password.</p>
              <div className="space-y-2">
                <Label htmlFor="forgotEmail">Email</Label>
                <Input id="forgotEmail" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForgotPassword(false); setForgotSent(false); }}>
              {forgotSent ? 'Close' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Install to Home Screen — always visible, contextual instructions */}
      {!isInstalled && (
        <div className="mt-4 text-center">
          {canInstall ? (
            <Button
              variant="outline"
              className="gap-2 text-primary border-primary/40 hover:bg-primary/5"
              onClick={triggerInstall}
              data-testid="pwa-install-btn"
            >
              <Smartphone className="w-4 h-4" />
              Add to Home Screen
            </Button>
          ) : isIOS ? (
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
              <Apple className="w-3.5 h-3.5" />
              Tap <strong className="mx-0.5">Share</strong> then <strong className="mx-0.5">"Add to Home Screen"</strong> to install
            </p>
          ) : (
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
              <Smartphone className="w-3.5 h-3.5" />
              Install as an app — open in Chrome and tap the install icon in the address bar
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default LoginPage;
